import { type Library, type LibraryMemberWithInfo } from '@air/api/types';
import { CheckIcon, XIcon } from '@air/design-icons';
import { ButtonGroup } from '@air/primitive-button-group';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { formatFullName } from '@air/utilities';
import { convertUnknownToError } from '@air/utils-error';

import { useLibraryApproveMemberMutation } from '~/components/LibraryBeta/hooks/mutations/useLibraryMemberApproveMutation';
import { useLibraryMembersDenyMutation } from '~/components/LibraryBeta/hooks/mutations/useLibraryMemberDenyMutation';
import { useLibraryToasts } from '~/hooks/useLibraryToasts';

export type LibraryPendingActionsProps = {
  libraryId: Library['id'];
  member: LibraryMemberWithInfo;
};

export const LibraryPendingActions = ({ libraryId, member }: LibraryPendingActionsProps) => {
  const { showLibraryToast } = useLibraryToasts();
  const { libraryMembersDenyMutation } = useLibraryMembersDenyMutation({ libraryId });
  const { libraryMemberApproveMutation } = useLibraryApproveMemberMutation({ libraryId });

  return (
    <ButtonGroup>
      <Tooltip label="Deny join request">
        <IconButton
          appearance="filled"
          adsIcon
          color="gray"
          icon={XIcon}
          label="Deny"
          isLoading={libraryMembersDenyMutation.isPending}
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
          onClick={() => {
            const fullName = formatFullName(member.firstName, member.lastName);

            libraryMembersDenyMutation.mutate([{ accountId: member.accountId }], {
              onError: (error) => showLibraryToast({ message: convertUnknownToError(error).message, type: 'error' }),
              onSuccess: () =>
                showLibraryToast({
                  message: (
                    <>
                      Denied <b className="ads:text-accent text-jay-200">{fullName}</b> join request.
                    </>
                  ),
                }),
            });
          }}
        />
      </Tooltip>
      <Tooltip label="Approve join request">
        <IconButton
          appearance="filled"
          adsIcon
          color="gray"
          icon={CheckIcon}
          label="Approve"
          isLoading={libraryMemberApproveMutation.isPending}
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
          onClick={() => {
            const fullName = formatFullName(member.firstName, member.lastName);

            libraryMemberApproveMutation.mutate(
              { accountId: member.accountId },
              {
                onError: (error) => showLibraryToast({ message: convertUnknownToError(error).message, type: 'error' }),
                onSuccess: () =>
                  showLibraryToast({
                    message: (
                      <>
                        Approved <b className="ads:text-accent text-jay-200">{fullName}</b> join request.
                      </>
                    ),
                  }),
              },
            );
          }}
        />
      </Tooltip>
    </ButtonGroup>
  );
};
